import React, { useEffect, useState } from "react";
import { enqueueSnackbar } from "notistack";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../../ui/card";
import { Input } from "../../ui/input";
import { Label } from "../../ui/label";
import { Button } from "../../ui/button";
import { Switch } from "../../ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../ui/select";
import { Loader2, MousePointerClick, X } from "lucide-react";
import { VariavelWiredSelect } from "../../VariavelWiredSelect";
import { WIRED_HUD_ICONES, WiredConexao, WiredHudAcao, wiredService } from "../../../services/wired";

interface WiredHudAcaoModalProps {
  isOpen: boolean;
  onClose: () => void;
  acao: WiredHudAcao | null;
  conexoes: WiredConexao[];
  onSuccess?: () => void;
}

const SEM_ICONE = "none";
const NOME_VARIAVEL_REGEX = /^[\w.-]{1,128}$/;
const INTEIRO_REGEX = /^-?\d{1,18}$/;

export function WiredHudAcaoModal({ isOpen, onClose, acao, conexoes, onSuccess }: WiredHudAcaoModalProps) {
  const [submitting, setSubmitting] = useState(false);
  const [idConexao, setIdConexao] = useState("");
  const [rotulo, setRotulo] = useState("");
  const [icone, setIcone] = useState(SEM_ICONE);
  const [disparo, setDisparo] = useState("");
  const [valorDisparo, setValorDisparo] = useState("1");
  const [requisito, setRequisito] = useState("");
  const [requisitoMinimo, setRequisitoMinimo] = useState("1");
  const [ativo, setAtivo] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!isOpen) return;
    setIdConexao(acao?.id_conexao ?? conexoes[0]?.id ?? "");
    setRotulo(acao?.rotulo ?? "");
    setIcone(acao?.icone ?? SEM_ICONE);
    setDisparo(acao?.nome_variavel_disparo ?? "");
    setValorDisparo(acao?.valor_disparo ?? "1");
    setRequisito(acao?.nome_variavel_requisito ?? "");
    setRequisitoMinimo(acao?.requisito_minimo ?? "1");
    setAtivo(acao?.ativo ?? true);
    setError("");
  }, [isOpen, acao, conexoes]);

  const validar = (): string => {
    if (!idConexao) return "Selecione uma conexão.";
    if (!rotulo.trim() || rotulo.trim().length > 60) return "Rótulo é obrigatório (máximo 60 caracteres).";
    if (!NOME_VARIAVEL_REGEX.test(disparo.trim())) return "Variável de disparo inválida (letras, números, _, - e .).";
    if (!INTEIRO_REGEX.test(valorDisparo.trim()) || BigInt(valorDisparo.trim()) === BigInt(0)) {
      return "O valor de disparo deve ser um inteiro diferente de 0 (0 é o valor neutro do jogo).";
    }
    if (requisito.trim()) {
      if (!NOME_VARIAVEL_REGEX.test(requisito.trim())) return "Variável de requisito inválida.";
      if (!INTEIRO_REGEX.test(requisitoMinimo.trim())) return "O mínimo do requisito deve ser um inteiro.";
    }
    return "";
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const mensagem = validar();
    if (mensagem) {
      setError(mensagem);
      return;
    }

    const payload = {
      id_conexao: idConexao,
      rotulo: rotulo.trim(),
      icone: icone === SEM_ICONE ? null : icone,
      nome_variavel_disparo: disparo.trim(),
      valor_disparo: valorDisparo.trim(),
      nome_variavel_requisito: requisito.trim() || null,
      requisito_minimo: requisitoMinimo.trim() || "1",
      ativo,
    };

    try {
      setSubmitting(true);
      if (acao) {
        await wiredService.atualizarHudAcao(acao.id, payload);
        enqueueSnackbar("Ação atualizada com sucesso!", { variant: "success" });
      } else {
        await wiredService.criarHudAcao(payload);
        enqueueSnackbar("Ação criada com sucesso!", { variant: "success" });
      }
      onClose();
      if (onSuccess) onSuccess();
    } catch (err: any) {
      enqueueSnackbar(err.response?.data?.message || "Erro ao salvar ação", { variant: "error" });
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <Card className="w-full max-w-lg mx-4 max-h-[90vh] overflow-y-auto">
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-4">
          <div>
            <CardTitle className="flex items-center gap-2">
              <MousePointerClick className="h-5 w-5 text-[oklch(0.45_0.15_142)]" />
              {acao ? "Editar ação" : "Nova ação"}
            </CardTitle>
            <CardDescription>Botão do HUD que dispara uma variável no jogo</CardDescription>
          </div>
          <Button variant="ghost" size="sm" onClick={onClose} className="h-6 w-6 p-0" disabled={submitting}>
            <X className="h-4 w-4" />
          </Button>
        </CardHeader>

        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label>Conexão</Label>
              <Select value={idConexao} onValueChange={setIdConexao} disabled={submitting}>
                <SelectTrigger>
                  <SelectValue placeholder="Selecione uma conexão" />
                </SelectTrigger>
                <SelectContent>
                  {conexoes.map((conexao) => (
                    <SelectItem key={conexao.id} value={conexao.id}>
                      {conexao.nome} (quarto {conexao.room_id}){!conexao.ativo ? " - inativa" : ""}
                      {!conexao.possui_chave_escrita ? " - sem chave de escrita" : ""}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="acao_rotulo">Rótulo do botão</Label>
                <Input
                  id="acao_rotulo"
                  value={rotulo}
                  onChange={(e) => {
                    setRotulo(e.target.value);
                    setError("");
                  }}
                  placeholder="Ex: Usar kit"
                  disabled={submitting}
                />
              </div>
              <div className="space-y-2">
                <Label>Ícone</Label>
                <Select value={icone} onValueChange={setIcone} disabled={submitting}>
                  <SelectTrigger>
                    <SelectValue placeholder="Sem ícone" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={SEM_ICONE}>Sem ícone</SelectItem>
                    {WIRED_HUD_ICONES.map((nome) => (
                      <SelectItem key={nome} value={nome}>
                        {nome}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-3 rounded-lg border border-border p-3">
              <div>
                <Label>Disparo no jogo</Label>
                <p className="text-xs text-muted-foreground">
                  Ao clicar, o servidor grava este valor na variável do próprio militar. O jogo deve reagir e voltar a
                  variável para 0; enquanto ela não for 0, novos cliques são recusados.
                </p>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div className="space-y-2 sm:col-span-2">
                  <Label htmlFor="acao_disparo">Variável</Label>
                  <VariavelWiredSelect
                    id="acao_disparo"
                    value={disparo}
                    onChange={(v) => {
                      setDisparo(v);
                      setError("");
                    }}
                    idConexao={idConexao}
                    ativo={isOpen}
                    placeholder="Buscar variável (ex: api_gerenciar_inventario)"
                    disabled={submitting}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="acao_valor">Valor</Label>
                  <Input
                    id="acao_valor"
                    value={valorDisparo}
                    onChange={(e) => {
                      setValorDisparo(e.target.value);
                      setError("");
                    }}
                    inputMode="numeric"
                    className="font-mono"
                    disabled={submitting}
                  />
                </div>
              </div>
            </div>

            <div className="space-y-3 rounded-lg border border-border p-3">
              <div>
                <Label>Requisito (opcional)</Label>
                <p className="text-xs text-muted-foreground">
                  O servidor só dispara se esta variável do militar tiver pelo menos o mínimo (ex.: item_kit ≥ 1). O
                  consumo do item continua sendo feito pelo jogo.
                </p>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div className="space-y-2 sm:col-span-2">
                  <Label htmlFor="acao_requisito">Variável</Label>
                  <VariavelWiredSelect
                    id="acao_requisito"
                    value={requisito}
                    onChange={(v) => {
                      setRequisito(v);
                      setError("");
                    }}
                    idConexao={idConexao}
                    ativo={isOpen}
                    placeholder="Buscar variável (ex: item_kit)"
                    disabled={submitting}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="acao_minimo">Mínimo</Label>
                  <Input
                    id="acao_minimo"
                    value={requisitoMinimo}
                    onChange={(e) => {
                      setRequisitoMinimo(e.target.value);
                      setError("");
                    }}
                    inputMode="numeric"
                    className="font-mono"
                    disabled={submitting || !requisito.trim()}
                  />
                </div>
              </div>
            </div>

            <div className="flex items-center justify-between rounded-lg border border-border p-3">
              <Label htmlFor="acao_ativo">Ativa</Label>
              <Switch id="acao_ativo" checked={ativo} onCheckedChange={setAtivo} disabled={submitting} />
            </div>

            {error && <p className="text-sm text-red-600">{error}</p>}

            <div className="flex gap-2 pt-4">
              <Button type="button" variant="outline" onClick={onClose} className="flex-1" disabled={submitting}>
                Cancelar
              </Button>
              <Button
                type="submit"
                disabled={submitting}
                className="flex-1 bg-[oklch(0.45_0.15_142)] hover:bg-[oklch(0.40_0.15_142)] text-white"
              >
                {submitting ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Salvando...
                  </>
                ) : acao ? (
                  "Atualizar"
                ) : (
                  "Criar"
                )}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
